import { useFormikContext } from 'formik';
import get from 'lodash/get';
import prettyBytes from 'pretty-bytes';
import { InputHTMLAttributes, ReactNode, useId, useRef, useState } from 'react';
import type { Accept, FileRejection } from 'react-dropzone';
import { useDropzone, ErrorCode } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Check, Prohibit, Trash, UploadSimple } from '@phosphor-icons/react';

import FormErrorMessage from '@tmh-website/components/shared/FormErrorMessage';
import TextFadeOverflowScroll from '@tmh-website/components/shared/TextFadeOverflowScroll';
import { UploadFile, transformFilesToUploadFiles } from '@tmh-website/services/upload.service';
import { Color } from '@tmh-website/shared/colors';
import StyledTypo from '@tmh-website/styles/typo';
import { FormElementCommonProps } from '@tmh-website/ui/FormElements';
import { StyledFormElements } from '@tmh-website/ui/FormElements';
import LoadingSpinner from '@tmh-website/ui/LoadingSpinner';

import { formElementThemes } from '@tmh-website/ui/FormElements';

import * as Styled from './FileUploader.styles';

export interface FileUploaderProps
  extends FormElementCommonProps,
    Partial<Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'onDrop' | 'accept'>> {
  helpText?: string | ReactNode;
  containerRef?: StyledFormElements.ContainerProps['ref'];
  centeredText?: boolean;
  smallLabel?: boolean;
  format?: (value: string | number) => string | number;
  /**
   * A function that runs when removing a file. Used to e.g. delete it on the server.
   */
  onRemove?: (file: UploadFile) => Promise<void>;
  /**
   * Callback that needs to return the url of the uploaded file
   */
  onDrop: (acceptedFiles: UploadFile[], rejected: FileRejection[]) => Promise<UploadFile | UploadFile[] | null>;
  /**
   * Define which filetypes are allowed. The key is the MIME type and the value is the file extension.
   * @see https://react-dropzone.js.org/#section-accepting-specific-file-types
   */
  accept?: Accept;
  /**
   * Allow multiple files to be uploaded
   */
  multiple?: boolean;
  maxFiles?: number;
  /**
   * Restrict size of files in bytes
   */
  minSize?: number;
  /**
   * Restrict size of files in bytes
   */
  maxSize?: number;
}

const FileUploader = ({
  themeColor = Color.Neutral_0_White,
  disabled,
  helpText,
  label,
  name,
  containerRef,
  required,
  placeholder,
  smallLabel,
  centeredText,
  format,
  onRemove,
  onDrop,
  multiple = false,
  accept,
  maxFiles,
  minSize,
  maxSize,
  ...props
}: FileUploaderProps) => {
  const uniqueId = useId();
  const nameWithId = name + '_' + uniqueId;
  const { t } = useTranslation();
  const { errors, touched, setFieldValue, values } = useFormikContext<any>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState<'single' | 'multiple' | false>(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadFile[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
  const value = get(values, name);
  const hasError = !!((get(errors, name) && get(touched, name)) || !!dropError || rejectedFiles.length > 0);
  const maxFilesLimit = multiple ? (maxFiles ? (maxFiles === 0 ? Infinity : maxFiles) : Infinity) : 1;

  async function handleRemove(file: UploadFile) {
    try {
      if (onRemove) {
        await onRemove(file);
      }
      setUploadedFiles(uploadedFiles.filter(f => f.id !== file.id));
      const newValue = multiple ? value.filter((v: string) => v !== file.responseFile?.url) : '';
      setFieldValue(name, newValue);
    } catch (e: any) {
      setDropError(e.message);
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (accepted, rejected) => {
      // Reset errors
      setDropError(null);

      // Check if the number of files is within the limit
      if (uploadedFiles.length + accepted.length + rejected.length > maxFilesLimit) {
        setDropError(
          multiple
            ? t('global:dropzone.error.maxFiles', { maxFiles: maxFilesLimit })
            : t('global:dropzone.error.maxFilesOne')
        );
        return;
      }

      setRejectedFiles(rejected);

      // Upload files
      try {
        if (accepted && accepted.length > 0) {
          const filesForUpload = transformFilesToUploadFiles(accepted);
          setIsLoading(filesForUpload.length > 1 ? 'multiple' : 'single');
          const uploadedFiles = await onDrop(filesForUpload, rejected);

          // Store new files in state and value
          if (!uploadedFiles || (Array.isArray(uploadedFiles) && uploadedFiles.length === 0)) {
            return;
          }
          const newFiles = Array.isArray(uploadedFiles) ? uploadedFiles : [uploadedFiles];
          setUploadedFiles(currentFiles => [...currentFiles, ...newFiles]);
          setFieldValue(name, multiple ? newFiles.map(file => file.responseFile?.url) : newFiles[0].responseFile?.url);
        }
      } catch (e: any) {
        setDropError(e.message);
      } finally {
        setIsLoading(false);
      }
    },
    multiple,
    accept,
    maxFiles,
    minSize,
    maxSize,
  });

  return (
    <Styled.FileUploaderContainer>
      {label && (
        <Styled.AdjustedLabel $themeColor={themeColor} htmlFor={nameWithId}>
          {label}
          {required && '*'}
        </Styled.AdjustedLabel>
      )}
      <Styled.Dropzone
        {...getRootProps()}
        $isDragActive={isDragActive}
        $hasError={hasError}
        $themeColor={themeColor}
        $isLoading={!!isLoading}
        $isSuccess={uploadedFiles.length === maxFilesLimit}
        $isDisabled={!!disabled}>
        <input
          name={name}
          disabled={disabled}
          id={nameWithId}
          placeholder={placeholder}
          {...props}
          value={undefined}
          type='file'
          ref={inputRef}
          {...getInputProps()}
        />
        {isLoading ? (
          <>
            <LoadingSpinner size={24} color={formElementThemes[themeColor].text} />
            {maxFiles && maxFiles !== 0 && maxFiles !== 1 && (
              <StyledTypo.Typography $style='uiText'>
                {isLoading === 'single' ? t('global:dropzone.uploadingSingle') : t('global:dropzone.uploadingMultiple')}
              </StyledTypo.Typography>
            )}
          </>
        ) : hasError ? (
          <>
            <Prohibit size={24} />
            <StyledTypo.Typography $style='uiText'>{t('global:dropzone.uploadAgainPrompt')}</StyledTypo.Typography>
          </>
        ) : uploadedFiles.length === maxFilesLimit ? (
          <>
            <Check size={24} />
            <StyledTypo.Typography $style='uiText'>{t('global:dropzone.infoMaxFiles')}</StyledTypo.Typography>
          </>
        ) : (
          <>
            <UploadSimple size={24} />
            <StyledTypo.Typography $style='uiText'>{t('global:dropzone.uploadPrompt')}</StyledTypo.Typography>
          </>
        )}
      </Styled.Dropzone>
      {uploadedFiles.length > 0 && (
        <Styled.FileList aria-label={t('global:dropzone.uploadedFiles') as string}>
          {uploadedFiles.map((file, index) => (
            <Styled.FileListEntry key={file.id} as='li' $style='uiText' $themeColor={themeColor}>
              <TextFadeOverflowScroll fadeColor={formElementThemes[themeColor].surface.normal}>
                {file.file.name}
              </TextFadeOverflowScroll>
              <Styled.FileListEntryRemove onClick={() => handleRemove(file)}>
                <Trash size={20} weight='regular' />
              </Styled.FileListEntryRemove>
            </Styled.FileListEntry>
          ))}
        </Styled.FileList>
      )}
      {helpText && <StyledFormElements.HelpText $themeColor={themeColor}>{helpText}</StyledFormElements.HelpText>}
      {dropError && <StyledFormElements.ErrorMessage>{dropError}</StyledFormElements.ErrorMessage>}
      {hasError && (
        <StyledFormElements.ErrorMessage>
          <FormErrorMessage name={name} />
        </StyledFormElements.ErrorMessage>
      )}
      {rejectedFiles.length > 0 &&
        rejectedFiles.map(file => {
          const errors = file.errors.map(error => {
            // Use custom error messages if provided
            if (error.code === ErrorCode.FileTooLarge) {
              return t('global:dropzone.error.maxSize', { maxSize: prettyBytes(maxSize ?? 0) });
            } else if (error.code === ErrorCode.FileTooSmall) {
              return t('global:dropzone.error.minSize', { minSize: prettyBytes(minSize ?? 0) });
            } else if (error.code === ErrorCode.FileInvalidType) {
              return t('global:dropzone.error.fileType', {
                fileTypes:
                  Object.values(accept ?? {})
                    .join(', ')
                    .replace(',.', ', .') ?? '',
              });
            }

            return error.message;
          });

          return (
            <StyledFormElements.ErrorMessage key={file.file.name}>
              {file.file.name}: {errors.join(' ')}
            </StyledFormElements.ErrorMessage>
          );
        })}
    </Styled.FileUploaderContainer>
  );
};

export default FileUploader;
